import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  X, Sparkles, ArrowLeft
} from 'lucide-react';
import Sidebar from '../components/Sidebar';

// Import existing components
import TaskPanel from '../components/tasks/TaskPanel';
import DocumentWorkspace from '../components/documents/DocumentWorkspace';
import CaseOverview from '../components/CaseOverviewNew';
import TeamChat from '../components/TeamChat';
import AgentDashboard from '../components/agents/AgentDashboard';
import PhaseManager from '../components/PhaseManager';
import CollaborationPanel from '../components/CollaborationPanel';
import PlanView from '../components/case/PlanView';
import SubcasesPanel from '../components/teams/SubcasesPanel';
import ConflictTabView from '../components/conflicts/ConflictTabView';
import KycTabView from '../components/kyc/KycTabView';
import WorkspaceShell from '../components/ai-workspace/WorkspaceShell';
import ActivityTimeline from '../components/ActivityTimeline';
import ClientCommunicationHub from '../components/case-workspace/ClientCommunicationHub';

// Import refactored components
import CaseSidebarNav from '../components/case-workspace/CaseSidebarNav';
import TimeTrackingTab from '../components/case-workspace/TimeTrackingTab';
import { useCaseWorkspace } from '../hooks/useCaseWorkspace';
import { useTimeTracking } from '../contexts/TimeTrackingContext';

const CaseWorkspaceWorldClass: React.FC = () => {
  const { caseId } = useParams<{ caseId: string }>();
  const navigate = useNavigate();
  
  // Time tracking context
  const { startCaseTracking } = useTimeTracking();
  
  // Use custom hook for data fetching
  const {
    caseData,
    timeEntries,
    tasks,
    documents,
    client,
    loading,
    fetchCaseData
  } = useCaseWorkspace(caseId);
  
  // Start time tracking when entering case page
  useEffect(() => {
    // Use topic or title, whichever is available
    const caseName = caseData?.topic || caseData?.title;
    
    // Debug logging
    console.log('🕐 Case tracking check:', { 
      caseId, 
      caseName, 
      hasData: !!caseData,
      loading 
    });
    
    // Only start tracking when we have case data loaded
    if (!loading && caseId && caseName) {
      // Start tracking for this case (startCaseTracking handles deduplication internally)
      startCaseTracking(caseId, caseName);
      console.log('✅ Called startCaseTracking for:', caseName);
    }
  }, [caseId, caseData, loading, startCaseTracking]);
  
  // Local UI state
  const [activeTab, setActiveTab] = useState('overview');
  const [mainSidebarExpanded, setMainSidebarExpanded] = useState(false);
  const [caseSidebarCollapsed, setCaseSidebarCollapsed] = useState(false);

  // Calculate counts for badges
  const pendingTaskCount = tasks?.filter((t: any) => !t.completed)?.length || 0;
  const unreadActivityCount = 0; // TODO: implement
  const newDocumentCount = 0; // TODO: implement

  // Loading state
  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <div className="w-6 h-6 border-2 border-slate-300 border-t-gray-900 rounded-full animate-spin mx-auto mb-3" />
          <p className="text-slate-500 text-sm">Laster sak...</p>
        </div>
      </div>
    );
  }

  // Case not found state
  if (!caseData) {
    return (
      <div className="h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <div className="w-12 h-12 bg-slate-100 rounded-xl flex items-center justify-center mx-auto mb-4">
            <X className="w-6 h-6 text-slate-400" />
          </div>
          <h2 className="text-lg font-semibold text-slate-900 mb-2">Fant ikke sak</h2>
          <button
            onClick={() => navigate('/lexy/dashboard?tab=cases')}
            className="text-slate-500 hover:text-slate-900 text-sm font-medium"
          >
            ← Tilbake til saker
          </button>
        </div>
      </div>
    );
  }

  // Render tab content
  const renderContent = () => {
    switch (activeTab) {
      case 'overview':
        return <CaseOverview caseData={caseData} timeEntries={timeEntries} tasks={tasks} documents={documents} />;
      case 'ai-workspace':
        return <WorkspaceShell caseId={caseId} caseName={caseData.topic} activeTab="chat" />;
      case 'tasks':
        return <TaskPanel caseId={caseId} onUpdate={fetchCaseData} />;
      case 'documents':
        return <DocumentWorkspace caseId={caseId || ''} caseData={caseData} documents={documents} onDocumentsChanged={fetchCaseData} />;
      case 'time':
        return <TimeTrackingTab caseId={caseId} timeEntries={timeEntries} onTimeAdded={fetchCaseData} />;
      case 'phases':
        return <PhaseManager caseId={caseId} />;
      case 'plan':
        return <PlanView caseId={caseId} />;
      case 'activity':
        return <ActivityTimeline caseId={caseId} />;
      case 'chat':
        return <ClientCommunicationHub caseId={caseId} caseData={caseData} client={client} documents={documents} />;
      case 'team-chat':
        return <TeamChat caseId={caseId} />;
      case 'collaboration':
        return <CollaborationPanel caseId={caseId} />;
      case 'agents':
        return <AgentDashboard caseId={caseId} />;
      case 'subcases':
        return <SubcasesPanel caseId={caseId} />;
      case 'conflicts':
        return <ConflictTabView caseId={caseId} />;
      case 'kyc':
        return <KycTabView caseId={caseId} />;
      default:
        return <CaseOverview caseData={caseData} timeEntries={timeEntries} tasks={tasks} documents={documents} />;
    }
  };

  return (
    <div className="h-screen flex bg-white overflow-hidden">
      {/* Global Sidebar */}
      <Sidebar 
        activeView="cases" 
        onNavigate={(view) => navigate(`/lexy/dashboard?tab=${view}`)}
        isExpanded={mainSidebarExpanded}
        onToggle={() => setMainSidebarExpanded(!mainSidebarExpanded)}
      />
      
      {/* Main Content Area */}
      <div className={`flex-1 flex overflow-hidden transition-all duration-300 ${mainSidebarExpanded ? 'ml-56' : 'ml-14'}`}>
        
        {/* Case Sidebar Nav */}
        <CaseSidebarNav
          caseData={caseData}
          activeTab={activeTab}
          onTabChange={setActiveTab}
          taskCount={pendingTaskCount}
          unreadActivityCount={unreadActivityCount}
          newDocumentCount={newDocumentCount}
          collapsed={caseSidebarCollapsed}
          onToggleCollapse={() => setCaseSidebarCollapsed(!caseSidebarCollapsed)}
        />

        {/* Content Area */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Minimal top bar - hidden for AI workspace (has its own header) */}
          {activeTab !== 'ai-workspace' && (
            <header className="h-12 bg-white border-b border-slate-100 px-4 flex items-center gap-3 flex-shrink-0">
              <button
                onClick={() => navigate('/lexy/dashboard?tab=cases')}
                className="flex items-center gap-2 text-sm text-slate-400 hover:text-slate-600 transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                <span>Tilbake</span>
              </button>
              <div className="w-px h-5 bg-slate-200" />
              <span className="text-sm font-medium text-slate-900 truncate">{caseData.topic}</span>
            </header>
          )}

          {/* Main content */}
          <main className="flex-1 overflow-y-auto overflow-x-hidden bg-white">
            {renderContent()}
          </main>
        </div>
      </div>
    </div>
  );
};

export default CaseWorkspaceWorldClass;
